import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { api } from "../../lib/config";
import { AdminLayout } from "./AdminShell";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { MultiImageUploader } from "../../components/ImageUploader";
import {
  Trash2, Plus, Edit3, Search, Users, FileText, Download, X,
  MapPin, Hotel, Bed, Layers, ArrowLeft, Phone, Mail, CalendarDays,
} from "lucide-react";

const CLIENT_STATUSES = ["Inquiry", "Sent", "Booked", "Advance Paid", "Fully Paid", "Trip Completed"];
const STATUS_STYLE = {
  "Inquiry": "bg-owt-sand/60 text-owt-forest",
  "Sent": "bg-blue-100 text-blue-800",
  "Booked": "bg-amber-100 text-amber-800",
  "Advance Paid": "bg-orange-100 text-orange-800",
  "Fully Paid": "bg-emerald-100 text-emerald-800",
  "Trip Completed": "bg-owt-forest text-owt-surface",
};
const CUR_SYM = { INR: "\u20b9", USD: "$" };
const EMPTY_CLIENT = { name: "", phone: "", email: "", status: "Inquiry", notes: "" };
const emptyDay = (n) => ({ day: n, place: "", hotel: "", stay: "", photos: [] });
const EMPTY_ITIN = {
  client_id: "", place_name: "", duration_days: 1, start_date: "", end_date: "",
  currency: "INR", adults: 1, kids: 0, price_per_adult: 0, price_per_kid: 0,
  days: [emptyDay(1)], is_template: false, template_name: "", notes: "",
};

const fmt = (cur, v) => `${CUR_SYM[cur] || ""}${Number(v || 0).toLocaleString(cur === "USD" ? "en-US" : "en-IN")}`;

async function downloadItineraryPdf(it) {
  try {
    const r = await api.get(`/itineraries/${it.id}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${it.code}.pdf`; document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  } catch { toast.error("PDF generation failed"); }
}

function Field({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted mb-2">{label}</div>
      {children}
    </div>
  );
}

/* ============================ ITINERARY EDITOR (reusable) ============================ */
function ItineraryEditor({ initial, clients, templates, lockedClient, onClose, onSaved }) {
  const [edit, setEdit] = useState(() => JSON.parse(JSON.stringify(initial)));
  const [loadTpl, setLoadTpl] = useState("");
  const [alsoTpl, setAlsoTpl] = useState(false);
  const [tplName, setTplName] = useState("");
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const isTemplateDoc = edit.is_template; // editing / creating a pure template
  const total = Number(edit.adults || 0) * Number(edit.price_per_adult || 0) + Number(edit.kids || 0) * Number(edit.price_per_kid || 0);

  const applyTemplate = async (tid) => {
    setLoadTpl(tid);
    if (!tid) return;
    try {
      const { data: t } = await api.get(`/itineraries/${tid}`);
      const days = JSON.parse(JSON.stringify(t.days || [])); // deep copy — never mutate original
      setEdit((prev) => ({ ...prev, place_name: t.place_name || prev.place_name, duration_days: t.duration_days || prev.duration_days, days: days.length ? days : [emptyDay(1)] }));
      toast.success(`Prefilled from: ${t.template_name || t.title}`);
    } catch { toast.error("Could not load template"); }
  };

  const setDay = (i, f, v) => setEdit((p) => ({ ...p, days: p.days.map((d, x) => x === i ? { ...d, [f]: v } : d) }));
  const addDay = () => setEdit((p) => ({ ...p, days: [...p.days, emptyDay(p.days.length + 1)] }));
  const removeDay = (i) => setEdit((p) => ({ ...p, days: p.days.filter((_, x) => x !== i).map((d, x) => ({ ...d, day: x + 1 })) }));

  const save = async () => {
    if (isTemplateDoc && !edit.template_name?.trim()) { toast.error("Give the template a name"); return; }
    if (!isTemplateDoc && !edit.client_id) { toast.error("Select a client"); return; }
    if (!isTemplateDoc && alsoTpl && !tplName.trim()) { toast.error("Name the template you're also saving"); return; }
    setBusy(true);
    const base = {
      place_name: edit.place_name, duration_days: Number(edit.duration_days || 1),
      start_date: edit.start_date, end_date: edit.end_date, currency: edit.currency,
      adults: Number(edit.adults || 0), kids: Number(edit.kids || 0),
      price_per_adult: Number(edit.price_per_adult || 0), price_per_kid: Number(edit.price_per_kid || 0),
      days: (edit.days || []).map((d, i) => ({ ...d, day: i + 1, photos: d.photos || [] })),
      notes: edit.notes || "",
    };
    const payload = isTemplateDoc
      ? { ...base, is_template: true, template_name: edit.template_name, client_id: null }
      : { ...base, is_template: false, template_name: "", client_id: edit.client_id };
    try {
      if (edit.id) await api.put(`/itineraries/${edit.id}`, payload);
      else await api.post("/itineraries", payload);
      if (!isTemplateDoc && alsoTpl) {
        await api.post("/itineraries", { ...base, is_template: true, template_name: tplName.trim(), client_id: null });
        toast.success("Saved to client & added to templates");
      } else {
        toast.success("Saved");
      }
      onSaved && onSaved();
      onClose();
    } catch { toast.error("Save failed"); setBusy(false); }
  };

  const clientName = lockedClient ? lockedClient.name : (clients.find((c) => c.id === edit.client_id)?.name || "");

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center overflow-y-auto z-50 p-4" onClick={onClose}>
      <div className="bg-owt-surface border border-owt-border w-full max-w-3xl my-8 p-8" onClick={(e) => e.stopPropagation()} data-testid="itinerary-form">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl text-owt-forest">
            {edit.id ? `Edit ${edit.code || "itinerary"}` : (isTemplateDoc ? "New template" : "New itinerary")}
            {!isTemplateDoc && clientName && <span className="ml-2 text-owt-muted text-base">· for {clientName}</span>}
          </h2>
          <button onClick={onClose} className="text-owt-muted hover:text-owt-forest"><X className="w-5 h-5" /></button>
        </div>

        {/* prefill from template — only when creating a NEW client itinerary */}
        {!edit.id && !isTemplateDoc && templates.length > 0 && (
          <div className="border border-owt-border bg-owt-sand/20 px-4 py-3 mb-6 flex flex-wrap items-center gap-3" data-testid="prefill-bar">
            <Layers className="w-4 h-4 text-owt-terracotta" />
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-forest">Reuse a saved template</span>
            <select value={loadTpl} onChange={(e) => applyTemplate(e.target.value)} data-testid="load-template" className="border border-owt-border px-3 py-2 text-sm bg-owt-surface md:ml-auto">
              <option value="">— start blank —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.template_name || t.title}</option>)}
            </select>
          </div>
        )}

        <div className="space-y-5">
          {isTemplateDoc ? (
            <Field label="Template name *"><Input value={edit.template_name} onChange={(e) => setEdit({ ...edit, template_name: e.target.value })} placeholder="e.g. Bali 5D Classic" data-testid="template-name" /></Field>
          ) : lockedClient ? (
            <div className="border border-owt-border bg-owt-forest/5 px-4 py-3 flex items-center gap-2" data-testid="locked-client">
              <Users className="w-4 h-4 text-owt-terracotta" />
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted">Client</span>
              <span className="text-owt-forest font-medium ml-2">{lockedClient.name}</span>
            </div>
          ) : (
            <Field label="Client *">
              <select value={edit.client_id || ""} onChange={(e) => setEdit({ ...edit, client_id: e.target.value })} data-testid="itinerary-client" className="w-full border border-owt-border px-3 py-2 text-sm bg-owt-surface">
                <option value="">— select client —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</option>)}
              </select>
            </Field>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Place / destination"><Input value={edit.place_name} onChange={(e) => setEdit({ ...edit, place_name: e.target.value })} data-testid="itinerary-place" /></Field>
            <Field label="Duration (days)"><Input type="number" min="1" value={edit.duration_days} onChange={(e) => setEdit({ ...edit, duration_days: e.target.value })} data-testid="itinerary-duration" /></Field>
            <Field label="Start date"><Input type="date" value={edit.start_date} onChange={(e) => setEdit({ ...edit, start_date: e.target.value })} data-testid="itinerary-start" /></Field>
            <Field label="End date"><Input type="date" value={edit.end_date} onChange={(e) => setEdit({ ...edit, end_date: e.target.value })} data-testid="itinerary-end" /></Field>
          </div>

          <div className="border border-owt-border p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-forest">Pricing</span>
              <div className="flex border border-owt-border">
                {["INR", "USD"].map((c) => (
                  <button key={c} type="button" onClick={() => setEdit({ ...edit, currency: c })} data-testid={`currency-${c}`} className={`px-3 py-1 font-mono text-[10px] tracking-[0.15em] ${edit.currency === c ? "bg-owt-forest text-owt-surface" : "text-owt-muted"}`}>{CUR_SYM[c]} {c}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Adults"><Input type="number" min="0" value={edit.adults} onChange={(e) => setEdit({ ...edit, adults: e.target.value })} data-testid="itinerary-adults" /></Field>
              <Field label="Price / adult"><Input type="number" min="0" value={edit.price_per_adult} onChange={(e) => setEdit({ ...edit, price_per_adult: e.target.value })} data-testid="itinerary-price-adult" /></Field>
              <Field label="Kids"><Input type="number" min="0" value={edit.kids} onChange={(e) => setEdit({ ...edit, kids: e.target.value })} data-testid="itinerary-kids" /></Field>
              <Field label="Price / kid"><Input type="number" min="0" value={edit.price_per_kid} onChange={(e) => setEdit({ ...edit, price_per_kid: e.target.value })} data-testid="itinerary-price-kid" /></Field>
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-owt-border">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted">Total trip cost</span>
              <span className="font-display text-2xl text-owt-forest" data-testid="itinerary-total">{fmt(edit.currency, total)}</span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-forest">Day-by-day breakdown</span>
              <button type="button" onClick={addDay} data-testid="add-day-btn" className="inline-flex items-center gap-1 border border-owt-forest text-owt-forest px-3 py-1.5 font-mono text-[9px] tracking-[0.15em] uppercase hover:bg-owt-forest hover:text-owt-surface transition-colors"><Plus className="w-3 h-3" /> Add day</button>
            </div>
            <div className="space-y-4">
              {edit.days.map((d, i) => (
                <div key={i} className="border border-owt-border p-4 bg-owt-bg/40" data-testid={`day-block-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-owt-terracotta">Day {i + 1}</span>
                    {edit.days.length > 1 && <button type="button" onClick={() => removeDay(i)} data-testid={`remove-day-${i}`} className="text-owt-muted hover:text-owt-terracotta"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label={<span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Place</span>}><Input value={d.place} onChange={(e) => setDay(i, "place", e.target.value)} data-testid={`day-place-${i}`} /></Field>
                    <Field label={<span className="flex items-center gap-1"><Hotel className="w-3 h-3" /> Hotel</span>}><Input value={d.hotel} onChange={(e) => setDay(i, "hotel", e.target.value)} data-testid={`day-hotel-${i}`} /></Field>
                  </div>
                  <div className="mt-4"><Field label={<span className="flex items-center gap-1"><Bed className="w-3 h-3" /> Stay / experience</span>}><Textarea rows={2} value={d.stay} onChange={(e) => setDay(i, "stay", e.target.value)} data-testid={`day-stay-${i}`} /></Field></div>
                  <div className="mt-4"><MultiImageUploader label="Day photos" value={d.photos} onChange={(urls) => setDay(i, "photos", urls)} /></div>
                </div>
              ))}
            </div>
          </div>

          <Field label="Internal notes"><Textarea rows={2} value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} data-testid="itinerary-notes" /></Field>

          {/* also save as template (client itineraries only) */}
          {!isTemplateDoc && (
            <label className="flex items-center gap-3 border border-owt-border bg-owt-sand/20 px-4 py-3 cursor-pointer" data-testid="also-template-row">
              <input type="checkbox" checked={alsoTpl} onChange={(e) => setAlsoTpl(e.target.checked)} data-testid="also-save-template" className="w-4 h-4 accent-owt-forest" />
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-forest flex items-center gap-1"><Layers className="w-3 h-3" /> Also save this as a reusable template</span>
              {alsoTpl && <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Template name" data-testid="also-template-name" className="md:ml-auto max-w-xs" />}
            </label>
          )}
        </div>

        <div className="flex gap-3 mt-6 pt-4 border-t border-owt-border flex-wrap">
          <button onClick={save} disabled={busy} data-testid="save-itinerary-btn" className="bg-owt-forest text-owt-surface px-6 py-3 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-owt-terracotta transition-colors disabled:opacity-50">{busy ? "Saving…" : (edit.id ? "Save changes" : "Save")}</button>
          {edit.id && <button onClick={async () => { setPdfBusy(true); await downloadItineraryPdf(edit); setPdfBusy(false); }} disabled={pdfBusy} className="inline-flex items-center gap-2 border border-owt-terracotta text-owt-terracotta px-6 py-3 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-owt-terracotta hover:text-owt-surface transition-colors disabled:opacity-50"><Download className="w-4 h-4" /> {pdfBusy ? "Preparing…" : "Download PDF"}</button>}
          <button onClick={onClose} className="border border-owt-border px-6 py-3 font-mono text-[11px] tracking-[0.2em] uppercase text-owt-muted hover:text-owt-forest">Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ============================ CLIENTS LIST ============================ */
export function AdminClients({ user }) {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const nav = useNavigate();

  const load = () => api.get("/clients", { params: q ? { q } : {} }).then((r) => setClients(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const save = async () => {
    if (!edit.name?.trim()) { toast.error("Name is required"); return; }
    try {
      if (edit.id) await api.put(`/clients/${edit.id}`, edit);
      else { const { data } = await api.post("/clients", edit); toast.success("Client added"); setEdit(null); nav(`/admin/clients/${data.id}`); return; }
      toast.success("Client saved"); setEdit(null); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this client and their itineraries?")) return;
    await api.delete(`/clients/${id}`); toast.success("Deleted"); load();
  };

  return (
    <AdminLayout user={user} active="clients">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl text-owt-forest flex items-center gap-3"><Users className="w-8 h-8 text-owt-terracotta" /> Clients</h1>
          <p className="text-owt-muted mt-1 font-mono text-[11px] tracking-[0.2em] uppercase">{clients.length} in your book · open a client to manage their trips</p>
        </div>
        <button onClick={() => setEdit({ ...EMPTY_CLIENT })} data-testid="new-client-btn" className="inline-flex items-center gap-2 bg-owt-forest text-owt-surface px-5 py-3 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-owt-terracotta transition-colors"><Plus className="w-4 h-4" /> New client</button>
      </div>

      <div className="relative mb-5 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-owt-muted" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone or email" data-testid="client-search" className="pl-9" />
      </div>

      <div className="border border-owt-border bg-owt-surface overflow-x-auto" data-testid="clients-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-owt-sand/40 text-left font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} onClick={() => nav(`/admin/clients/${c.id}`)} className="border-t border-owt-border hover:bg-owt-sand/20 cursor-pointer" data-testid={`client-row-${c.id}`}>
                <td className="px-4 py-3 font-medium text-owt-forest">{c.name}</td>
                <td className="px-4 py-3 text-owt-ink">{c.phone || "—"}</td>
                <td className="px-4 py-3 text-owt-ink">{c.email || "—"}</td>
                <td className="px-4 py-3"><span className={`px-2 py-1 text-[10px] font-mono tracking-wide uppercase ${STATUS_STYLE[c.status] || "bg-owt-sand"}`}>{c.status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={(e) => { e.stopPropagation(); nav(`/admin/clients/${c.id}`); }} data-testid={`open-client-${c.id}`} className="inline-flex items-center gap-1 border border-owt-forest text-owt-forest px-3 py-1.5 font-mono text-[9px] tracking-[0.15em] uppercase hover:bg-owt-forest hover:text-owt-surface transition-colors"><FileText className="w-3 h-3" /> Open</button>
                    <button onClick={(e) => { e.stopPropagation(); setEdit(c); }} data-testid={`edit-client-${c.id}`} className="p-2 text-owt-muted hover:text-owt-forest"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={(e) => del(e, c.id)} data-testid={`delete-client-${c.id}`} className="p-2 text-owt-muted hover:text-owt-terracotta"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {clients.length === 0 && (<tr><td colSpan={5} className="px-4 py-10 text-center text-owt-muted font-mono text-xs tracking-wide">No clients yet.</td></tr>)}
          </tbody>
        </table>
      </div>

      {edit && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center overflow-y-auto z-50 p-4" onClick={() => setEdit(null)}>
          <div className="bg-owt-surface border border-owt-border w-full max-w-lg mt-10 p-8" onClick={(e) => e.stopPropagation()} data-testid="client-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl text-owt-forest">{edit.id ? "Edit client" : "New client"}</h2>
              <button onClick={() => setEdit(null)} className="text-owt-muted hover:text-owt-forest"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <Field label="Name *"><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="client-name" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Phone"><Input value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} data-testid="client-phone" /></Field>
                <Field label="Email"><Input value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} data-testid="client-email" /></Field>
              </div>
              <Field label="Status">
                <select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} data-testid="client-status" className="w-full border border-owt-border px-3 py-2 text-sm bg-owt-surface">
                  {CLIENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Notes"><Textarea value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} rows={3} data-testid="client-notes" /></Field>
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={save} data-testid="save-client-btn" className="bg-owt-forest text-owt-surface px-6 py-3 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-owt-terracotta transition-colors">Save</button>
              <button onClick={() => setEdit(null)} className="border border-owt-border px-6 py-3 font-mono text-[11px] tracking-[0.2em] uppercase text-owt-muted hover:text-owt-forest">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

/* ============================ CLIENT DETAIL ============================ */
export function ClientDetail({ user }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [itins, setItins] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editorInitial, setEditorInitial] = useState(null);

  const load = () => {
    api.get(`/clients/${id}`).then((r) => setClient(r.data)).catch(() => nav("/admin/clients"));
    api.get("/itineraries", { params: { client_id: id } }).then((r) => setItins(r.data));
    api.get("/itineraries", { params: { is_template: true } }).then((r) => setTemplates(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (status) => {
    await api.put(`/clients/${id}`, { ...client, status });
    setClient({ ...client, status }); toast.success(`Status → ${status}`);
  };
  const delItin = async (it) => { if (!window.confirm(`Delete ${it.code}?`)) return; await api.delete(`/itineraries/${it.id}`); toast.success("Deleted"); load(); };

  if (!client) return <AdminLayout user={user} active="clients"><div className="font-mono text-sm text-owt-muted">Loading…</div></AdminLayout>;

  return (
    <AdminLayout user={user} active="clients">
      <button onClick={() => nav("/admin/clients")} className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted hover:text-owt-forest mb-6" data-testid="back-to-clients"><ArrowLeft className="w-3 h-3" /> All clients</button>

      {/* client card */}
      <div className="border border-owt-border bg-owt-surface p-6 md:p-8 mb-8">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-4xl text-owt-forest">{client.name}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-owt-ink">
              {client.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-owt-terracotta" /> {client.phone}</span>}
              {client.email && <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-owt-terracotta" /> {client.email}</span>}
            </div>
            {client.notes && <p className="text-owt-muted mt-3 text-sm max-w-xl">{client.notes}</p>}
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted mb-2">Status</div>
            <select value={client.status} onChange={(e) => setStatus(e.target.value)} data-testid="detail-client-status" className={`border border-owt-border px-3 py-2 text-sm font-mono tracking-wide ${STATUS_STYLE[client.status] || ""}`}>
              {CLIENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* itineraries for this client */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-display text-2xl text-owt-forest flex items-center gap-2"><FileText className="w-5 h-5 text-owt-terracotta" /> Itineraries ({itins.length})</h2>
        <button onClick={() => setEditorInitial({ ...EMPTY_ITIN, client_id: id, days: [emptyDay(1)] })} data-testid="create-itinerary-btn" className="inline-flex items-center gap-2 bg-owt-forest text-owt-surface px-5 py-3 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-owt-terracotta transition-colors"><Plus className="w-4 h-4" /> Create itinerary</button>
      </div>

      <div className="space-y-3" data-testid="client-itineraries">
        {itins.map((it) => (
          <div key={it.id} className="border border-owt-border bg-owt-surface p-4 flex items-center justify-between gap-4 flex-wrap hover:border-owt-terracotta transition-colors" data-testid={`client-itin-${it.id}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <span className="font-mono text-owt-terracotta text-sm">{it.code}</span>
                <span className="text-owt-forest font-medium">{it.title}</span>
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-1 text-xs text-owt-muted font-mono tracking-wide">
                {it.start_date && <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {it.start_date}{it.end_date ? ` → ${it.end_date}` : ""}</span>}
                <span>{it.adults || 0} adults · {it.kids || 0} kids</span>
                <span className="text-owt-forest font-medium">{fmt(it.currency, it.total_cost)}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => downloadItineraryPdf(it)} data-testid={`client-itin-pdf-${it.id}`} className="inline-flex items-center gap-1 border border-owt-terracotta text-owt-terracotta px-3 py-1.5 font-mono text-[9px] tracking-[0.15em] uppercase hover:bg-owt-terracotta hover:text-owt-surface transition-colors"><Download className="w-3 h-3" /> PDF</button>
              <button onClick={() => setEditorInitial(JSON.parse(JSON.stringify(it)))} data-testid={`client-itin-edit-${it.id}`} className="inline-flex items-center gap-1 bg-owt-forest text-owt-surface px-3 py-1.5 font-mono text-[9px] tracking-[0.15em] uppercase hover:bg-owt-terracotta transition-colors"><Edit3 className="w-3 h-3" /> Edit</button>
              <button onClick={() => delItin(it)} data-testid={`client-itin-del-${it.id}`} className="p-2 text-owt-muted hover:text-owt-terracotta"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {itins.length === 0 && (
          <div className="border border-dashed border-owt-border p-10 text-center" data-testid="no-itineraries">
            <p className="text-owt-muted font-mono text-xs tracking-wide">No itineraries yet for {client.name}.</p>
            <button onClick={() => setEditorInitial({ ...EMPTY_ITIN, client_id: id, days: [emptyDay(1)] })} className="mt-4 inline-flex items-center gap-2 text-owt-forest font-mono text-[10px] tracking-[0.2em] uppercase underline underline-offset-4 hover:text-owt-terracotta"><Plus className="w-3 h-3" /> Create the first one</button>
          </div>
        )}
      </div>

      {editorInitial && (
        <ItineraryEditor
          initial={editorInitial}
          clients={[client]}
          templates={templates}
          lockedClient={client}
          onClose={() => setEditorInitial(null)}
          onSaved={load}
        />
      )}
    </AdminLayout>
  );
}

/* ============================ GLOBAL ITINERARY SEARCH ============================ */
export function AdminItineraries({ user }) {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [q, setQ] = useState("");
  const [editorInitial, setEditorInitial] = useState(null);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const clientFilter = params.get("client") || "";

  const load = () => api.get("/itineraries", { params: { ...(q ? { q } : {}), ...(clientFilter ? { client_id: clientFilter } : {}) } }).then((r) => setItems(r.data));
  const loadMeta = () => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/itineraries", { params: { is_template: true } }).then((r) => setTemplates(r.data));
  };
  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, clientFilter]);

  const del = async (id) => { if (!window.confirm("Delete this itinerary?")) return; await api.delete(`/itineraries/${id}`); toast.success("Deleted"); load(); };
  const clientName = clients.find((c) => c.id === clientFilter)?.name;

  return (
    <AdminLayout user={user} active="itineraries">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl text-owt-forest flex items-center gap-3"><FileText className="w-8 h-8 text-owt-terracotta" /> All itineraries</h1>
          <p className="text-owt-muted mt-1 font-mono text-[11px] tracking-[0.2em] uppercase">{clientFilter && clientName ? `For ${clientName}` : `${items.length} total · search by ID or client`}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setEditorInitial({ ...EMPTY_ITIN, days: [emptyDay(1)] })} data-testid="new-itinerary-btn" className="inline-flex items-center gap-2 bg-owt-forest text-owt-surface px-5 py-3 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-owt-terracotta transition-colors"><Plus className="w-4 h-4" /> New itinerary</button>
          <button onClick={() => setEditorInitial({ ...EMPTY_ITIN, is_template: true, template_name: "", days: [emptyDay(1)] })} data-testid="new-template-btn" className="inline-flex items-center gap-2 border border-owt-forest text-owt-forest px-5 py-3 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-owt-forest hover:text-owt-surface transition-colors"><Layers className="w-4 h-4" /> New template</button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-owt-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by ID (OWT-1001) or client name" data-testid="itinerary-search" className="pl-9" />
        </div>
        {clientFilter && <button onClick={() => setParams({})} data-testid="clear-client-filter" className="inline-flex items-center gap-1 border border-owt-border px-3 py-2 font-mono text-[10px] tracking-[0.15em] uppercase text-owt-muted hover:text-owt-terracotta"><X className="w-3 h-3" /> Clear filter</button>}
      </div>

      <div className="border border-owt-border bg-owt-surface overflow-x-auto" data-testid="itineraries-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-owt-sand/40 text-left font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted">
              <th className="px-4 py-3">ID</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Total</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-owt-border hover:bg-owt-sand/20" data-testid={`itinerary-row-${it.id}`}>
                <td className="px-4 py-3 font-mono text-owt-terracotta">{it.code}{it.is_template && <span className="ml-2 text-[9px] bg-owt-forest text-owt-surface px-1.5 py-0.5">TPL</span>}</td>
                <td className="px-4 py-3 text-owt-forest">{it.title}</td>
                <td className="px-4 py-3 text-owt-ink">
                  {it.client_id ? <button onClick={() => nav(`/admin/clients/${it.client_id}`)} className="underline underline-offset-2 hover:text-owt-terracotta" data-testid={`goto-client-${it.id}`}>{it.client_name}</button> : <span className="text-owt-muted">—</span>}
                </td>
                <td className="px-4 py-3 text-owt-forest font-medium whitespace-nowrap">{fmt(it.currency, it.total_cost)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => downloadItineraryPdf(it)} data-testid={`pdf-${it.id}`} className="inline-flex items-center gap-1 border border-owt-terracotta text-owt-terracotta px-3 py-1.5 font-mono text-[9px] tracking-[0.15em] uppercase hover:bg-owt-terracotta hover:text-owt-surface transition-colors"><Download className="w-3 h-3" /> PDF</button>
                    <button onClick={() => setEditorInitial(JSON.parse(JSON.stringify(it)))} data-testid={`edit-itinerary-${it.id}`} className="p-2 text-owt-muted hover:text-owt-forest"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => del(it.id)} data-testid={`delete-itinerary-${it.id}`} className="p-2 text-owt-muted hover:text-owt-terracotta"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (<tr><td colSpan={5} className="px-4 py-10 text-center text-owt-muted font-mono text-xs tracking-wide">No itineraries found.</td></tr>)}
          </tbody>
        </table>
      </div>

      {editorInitial && (
        <ItineraryEditor
          initial={editorInitial}
          clients={clients}
          templates={templates}
          onClose={() => setEditorInitial(null)}
          onSaved={() => { load(); loadMeta(); }}
        />
      )}
    </AdminLayout>
  );
}
